import React from "react";
import { parseISO, isDate } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { Text } from "@/components/atoms/Text";
import { FormattedMessage } from "react-intl";
import { AvailabilityStatus } from "@/components/features/calendar/types";

interface AvailabilityBlockProps {
  availability: AvailabilityStatus;
  date: Date | string;
  timeZone: string;
}

const isValidTimeZone = (timeZone: string): boolean => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone });
    return true;
  } catch {
    return false;
  }
};

export const AvailabilityBlock: React.FC<AvailabilityBlockProps> = ({
  availability,
  date,
  timeZone
}) => {

  // Fallback to UTC if the timeZone is invalid
  const validatedTimeZone = isValidTimeZone(timeZone) ? timeZone : "UTC";


  const formatAvailability = (availability: string) => {
    switch (availability) {
      case "partially-booked":
        return (<FormattedMessage defaultMessage="Partially Booked" />)
      case "fully-booked":
        return (<FormattedMessage defaultMessage="Fully Booked" />)
      case "available":
      default:
        return (<FormattedMessage defaultMessage="Available" />)
    }
  };

  const parsedDate = isDate(date) ? date : parseISO(date);

  const month = formatInTimeZone(parsedDate, validatedTimeZone, "MMM");
  const dayNumber = formatInTimeZone(parsedDate, validatedTimeZone, "d");
  const day = formatInTimeZone(parsedDate, validatedTimeZone, "EEEE");

  return (
    <div className="tw-rounded-2 tw-w-full tw-p-12 sm:tw-px-16 tw-border tw-border-gray-500 tw-flex tw-gap-16 tw-items-center">
      <div className="tw-w-full">
        <Text typeface="headline-2-m" className="tw-block tw-mb-4">
          {`${month} ${dayNumber}`}
        </Text>
        <Text typeface="caption-2" className="tw-text-gray-600 tw-block">
          {day}
        </Text>
      </div>
      <Text typeface="body-2-m" className="tw-whitespace-nowrap">
        {formatAvailability(availability)}
      </Text>
    </div>
  );
};
